import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, ArrowRight } from "lucide-react";
import { API, fmt, imgW } from "../lib/api";

const SUGGESTIONS = ["J'adore le chocolat blanc", "Un cookie pas trop sucré", "Idée pour un anniversaire"];
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const PAGE_KEYS = { "/composer-ma-box": ["compos"], "/offrir": ["carte cadeau"], "/evenements": ["devis", "evenement"] };
const findLink = (label, links) => {
  const n = ` ${norm(label)} `;
  return links.find((l) => l.kind !== "page" && n.includes(` ${norm(l.name)} `))
    || links.find((l) => l.kind === "page" && (PAGE_KEYS[l.url] || []).some((k) => n.includes(k)));
};

function Bubble({ text, links, onNav }) {
  const lines = text.replace(/\*/g, "").split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <div className="space-y-1.5">
      {lines.map((l, i) => {
        const bullet = /^[•\-–]\s*/.test(l);
        const body = l.replace(/^[•\-–]\s*/, "");
        const m = bullet ? body.match(/^([^:]+?)\s*:\s*(.*)$/) : null;
        const hit = m && findLink(m[1], links);
        const title = hit ? <Link to={hit.url} onClick={onNav} data-testid={`chat-product-link-${i}`} className="font-semibold text-[#A8410F] underline underline-offset-2 hover:text-[#2B170C]">{m[1]}</Link> : m && <strong className="text-[#2B170C]">{m[1]}</strong>;
        return bullet ? (
          <div key={i} className="flex gap-2 rounded-xl bg-[#FBF3EA] px-2.5 py-1.5">
            <span className="text-[#C6531A] font-bold">•</span>
            <span>{m ? <>{title} — {m[2]}</> : body}</span>
          </div>
        ) : <p key={i}>{l}</p>;
      })}
    </div>
  );
}

function LinkChips({ links, onNav, idx }) {
  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid={`chat-links-${idx}`}>
      {links.map((l, i) => (
        <Link key={l.url + i} to={l.url} onClick={onNav} data-testid={`chat-link-${idx}-${i}`}
          className="flex items-center gap-2.5 rounded-xl border border-[#E6D7C3] bg-white px-2 py-1.5 hover:border-[#C6531A] transition-colors">
          {l.image ? <img src={imgW(l.image, 220)} width="36" height="36" alt="" loading="lazy" className="w-9 h-9 rounded-lg object-cover bg-[#F6EADF]" /> : <span className="w-9 h-9 rounded-lg bg-[#F6EADF] grid place-items-center text-[#A8410F]"><Sparkles size={16} /></span>}
          <span className="flex-1 min-w-0 text-[13px] font-semibold leading-tight text-[#2B170C]">{l.name}</span>
          {l.price > 0 && <span className="text-xs text-[#6E5546]">{fmt(l.price)}</span>}
          <ArrowRight size={15} className="text-[#A8410F] shrink-0" />
        </Link>
      ))}
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Coucou 🤎 Je suis ton assistant gourmand !\nDis-moi ce que tu aimes (chocolat, pistache, peu sucré…) et je te conseille le dessert parfait." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const onCart = /^\/(panier|commander|composer-ma-box)/.test(useLocation().pathname);
  const onNav = () => { if (window.innerWidth < 768) setOpen(false); };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || loading) return;
    const history = msgs.slice(1).map(({ role, content }) => ({ role, content }));
    const next = [...msgs, { role: "user", content: text }, { role: "assistant", content: "" }];
    setMsgs(next); setInput(""); setLoading(true);
    const patch = (fn) => setMsgs((cur) => { const c = [...cur]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const res = await fetch(`${API}/ai/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: text, history }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        patch((m) => ({ ...m, content: res.status === 429 ? (err.detail || "Trop de messages d'un coup, réessaie dans quelques minutes 🤎") : "Oups, réessaie dans un instant 🤎" }));
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          try {
            const ev = JSON.parse(d);
            if (ev.t) patch((m) => ({ ...m, content: m.content + ev.t }));
            if (ev.p) patch((m) => ({ ...m, links: ev.p }));
          } catch { /* ignore partial */ }
        }
      }
    } catch {
      patch((m) => ({ ...m, content: m.content + "Oups, réessaie dans un instant 🤎" }));
    } finally { setLoading(false); }
  };

  return (
    <>
      <button data-testid="chat-toggle" onClick={() => setOpen(!open)} aria-label={open ? "Fermer la discussion" : "Ouvrir la discussion"} aria-expanded={open}
        className={`fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 rounded-full btn-terra place-items-center shadow-[0_8px_30px_rgba(226,125,96,0.45)] ${onCart ? "hidden lg:grid" : "grid"}`}>
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-40 md:bottom-24 right-4 md:right-6 z-40 w-[calc(100vw-2rem)] max-w-sm h-[480px] bg-white rounded-3xl shadow-[0_20px_60px_rgba(43,23,12,0.25)] border border-[#E6D7C3] flex flex-col overflow-hidden animate-float-up">
          <div className="bg-[#3D2314] text-white px-5 py-4 flex items-center gap-2">
            <Sparkles size={18} className="text-[#D4A359]" />
            <div><p className="font-serif font-semibold leading-tight">Assistant gourmand</p><p className="text-[10px] text-[#D9BCA8]">Crunchy Sweet • IA</p></div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F4EAD9]">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`chat-msg-${i}`} className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#C6531A] text-white rounded-br-sm" : "bg-white border border-[#E6D7C3] text-[#2B170C] rounded-bl-sm"}`}>
                  {m.role === "user" ? m.content : (m.content ? <Bubble text={m.content} links={m.links || []} onNav={onNav} /> : <span className="text-[#7A6455]">…</span>)}
                  {m.role !== "user" && m.links?.length > 0 && <LinkChips links={m.links} onNav={onNav} idx={i} />}
                </div>
              </div>
            ))}
            {msgs.length === 1 && (
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#DCC6AE] text-[#A8440E] hover:border-[#C6531A] transition-colors">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#E6D7C3] flex gap-2">
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} aria-label="Ton message"
              placeholder="Ex : j'adore le chocolat…" className="flex-1 px-3.5 py-2.5 rounded-full border border-[#DCC6AE] text-sm focus:outline-none focus:border-[#C6531A]" />
            <button data-testid="chat-send" disabled={loading} onClick={() => send()} aria-label="Envoyer" className="btn-terra w-10 h-10 grid place-items-center rounded-full shrink-0 disabled:opacity-60"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
